import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ChevronDown, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const MEETING_IMG =
  "https://images.pexels.com/photos/5520284/pexels-photo-5520284.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ARCH_IMG =
  "https://images.unsplash.com/photo-1670003112050-710fbf757482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";

const SectionHead = ({ overline, titleA, titleB }) => (
  <Reveal>
    <p className="overline-label">{overline}</p>
    <h2 className="mt-4 max-w-3xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
      {titleA}
      <span className="font-serif italic text-brass">{titleB}</span>
    </h2>
  </Reveal>
);

const PastInvestments = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("portfolio");
  const list = t("past.list", { returnObjects: true });

  return (
    <div>
      <Reveal>
        <button
          data-testid="portfolio-toggle-past"
          onClick={() => setOpen((v) => !v)}
          className="btn-ghost"
          aria-expanded={open}
        >
          <Eye size={14} />
          {open ? t("past.hide") : t("past.show")}
          <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        <p className="mt-4 text-xs text-slate2/70">{t("past.hint")}</p>
      </Reveal>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div data-testid="past-investments-list" className="mt-8 border-t border-white/[0.08]">
              {list.map((p, i) => (
                <div
                  key={i}
                  data-testid={`past-investment-${i + 1}`}
                  className="grid gap-1 border-b border-white/[0.08] py-5 text-sm sm:grid-cols-[1fr_1fr_120px_100px_70px] sm:items-baseline sm:gap-6"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-brass/80">{p.sector}</span>
                  <span className="text-mist/85">{p.deal}</span>
                  <span className="text-slate2">{p.geography}</span>
                  <span className="font-mono text-xs text-chalk">{p.amount}</span>
                  <span className="font-mono text-xs text-slate2">{p.year}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Portfolio() {
  const { t } = useTranslation("portfolio");
  const stats = t("stats", { returnObjects: true });
  const classes = t("classes.items", { returnObjects: true });
  const instruments = t("instruments.items", { returnObjects: true });
  const startupsList = t("geo.startupsList", { returnObjects: true });
  const immoList = t("geo.immoList", { returnObjects: true });
  const acquired = t("acquired.items", { returnObjects: true });
  const assetTypes = t("assetTypes.items", { returnObjects: true });
  const criteria = t("criteria.items", { returnObjects: true });
  const sectors = t("sectors.items", { returnObjects: true });
  const art = t("art", { returnObjects: true });

  return (
    <div data-testid="portfolio-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          <Link to="/investissements" data-testid="portfolio-breadcrumb" className="transition-colors duration-200 hover:text-chalk">
            {t("breadcrumb")}
          </Link>
          <span className="text-slate2/50">/</span>
          <span>Portfolio</span>
        </p>
        <h1 className="max-w-4xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>

        <div className="mt-12 grid grid-cols-2 gap-px bg-white/[0.06] lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06} className="bg-ink">
              <div className="p-6 lg:p-8" data-testid={`portfolio-stat-${i + 1}`}>
                <p className="font-display text-2xl text-chalk lg:text-3xl">
                  {s.suffix && <span className="mr-1 font-mono text-xs uppercase tracking-[0.2em] text-brass">{s.suffix}</span>}
                  {s.value}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate2">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </header>

      <section className="mx-auto mt-24 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-32">
        <SectionHead overline={t("classes.overline")} titleA={t("classes.titleA")} titleB={t("classes.titleB")} />
        <Reveal>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("classes.lead")}</p>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
          {classes.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.06} className="bg-ink">
              <article
                data-testid={`asset-class-${c.id}`}
                className="flex h-full flex-col p-8 transition-colors duration-300 hover:bg-elevated"
              >
                <p className="font-display text-4xl text-brass">{c.share}</p>
                <h3 className="mt-5 font-display text-lg text-chalk">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate2">{c.description}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <SectionHead overline={t("instruments.overline")} titleA={t("instruments.titleA")} titleB={t("instruments.titleB")} />
          <Reveal>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("instruments.lead")}</p>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {instruments.map((ins, i) => (
              <Reveal key={ins.title} delay={i * 0.05}>
                <article
                  data-testid={`instrument-${i + 1}`}
                  className="h-full border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35"
                >
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-4 font-display text-lg text-chalk">{ins.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate2">{ins.description}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <SectionHead overline={t("geo.overline")} titleA={t("geo.titleA")} titleB={t("geo.titleB")} />
        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="flex h-full flex-col justify-between border border-brass/25 bg-elevated/40 p-8">
              <div>
                <p className="overline-label">{t("geo.minTicketLabel")}</p>
                <p className="mt-6 font-display text-5xl text-chalk">
                  1 M<span className="ml-2 font-mono text-sm uppercase tracking-[0.2em] text-brass">CHF</span>
                </p>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-slate2">{t("geo.minTicketNote")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-4">
            <div className="h-full border border-white/[0.08] bg-surface/40 p-8">
              <h3 className="font-display text-xl text-chalk">{t("geo.startupsTitle")}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate2">{t("geo.startupsDesc")}</p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {startupsList.map((g) => (
                  <li key={g} className="border border-white/[0.1] px-3 py-1.5 font-mono text-[11px] text-mist/75">
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4">
            <div className="h-full border border-white/[0.08] bg-surface/40 p-8">
              <h3 className="font-display text-xl text-chalk">{t("geo.immoTitle")}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate2">{t("geo.immoDesc")}</p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {immoList.map((g) => (
                  <li key={g} className="border border-white/[0.1] px-3 py-1.5 font-mono text-[11px] text-mist/75">
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="overflow-hidden border border-white/10">
                <img
                  src={MEETING_IMG}
                  alt={t("buyout.imgAlt")}
                  className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[400px]"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <p className="overline-label">{t("buyout.overline")}</p>
              <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
                {t("buyout.titleA")}
                <span className="font-serif italic text-brass">{t("buyout.titleB")}</span>
              </h2>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-mist/85">{t("buyout.lead")}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-slate2">
                {t("buyout.note")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <SectionHead overline={t("acquired.overline")} titleA={t("acquired.titleA")} titleB={t("acquired.titleB")} />
        <Reveal>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("acquired.lead")}</p>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {acquired.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <article
                data-testid={`acquired-card-${i + 1}`}
                className="flex h-full flex-col border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass/80">{c.category}</span>
                  <span className="border border-brass/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
                    {t("acquired.badge")}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-lg leading-snug text-chalk">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate2">{c.description}</p>
                <div className="mt-6 flex items-baseline justify-between border-t border-white/[0.08] pt-5">
                  <span className="font-display text-xl text-chalk">{c.amount}</span>
                  <span className="font-mono text-xs text-slate2">{c.country}</span>
                </div>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {c.tags.map((tag) => (
                    <li key={tag} className="border border-white/[0.1] px-2.5 py-1 font-mono text-[10px] text-mist/70">
                      {tag}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-8 text-xs italic text-slate2/70">{t("acquired.disclaimer")}</p>
        </Reveal>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <SectionHead overline={t("assetTypes.overline")} titleA={t("assetTypes.titleA")} titleB={t("assetTypes.titleB")} />
          <Reveal>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("assetTypes.lead")}</p>
          </Reveal>
          <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-5">
            {assetTypes.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05} className="bg-ink">
                <div data-testid={`asset-type-${i + 1}`} className="h-full p-6 transition-colors duration-300 hover:bg-elevated">
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-4 font-display text-base text-chalk">{a.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-slate2">{a.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("criteria.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("criteria.titleA")}
              <span className="font-serif italic text-brass">{t("criteria.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-mist/85">{t("criteria.lead")}</p>
            <div className="mt-10">
              {criteria.map((c) => (
                <div key={c.num} className="grid gap-2 border-t border-white/[0.08] py-6 last:border-b sm:grid-cols-[56px_1fr] sm:gap-8">
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">{c.num}</p>
                  <div>
                    <h3 className="font-display text-lg text-chalk">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate2">{c.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={ARCH_IMG}
                alt={t("criteria.imgAlt")}
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[420px]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <SectionHead overline={t("past.overline")} titleA={t("past.titleA")} titleB={t("past.titleB")} />
          <Reveal>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("past.lead")}</p>
          </Reveal>
          <div className="mt-10">
            <PastInvestments />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <SectionHead overline={t("sectors.overline")} titleA={t("sectors.titleA")} titleB={t("sectors.titleB")} />
        <Reveal>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("sectors.lead")}</p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {sectors.map((s) => (
              <li
                key={s}
                className="border border-white/[0.1] px-3.5 py-1.5 font-mono text-[11px] tracking-[0.08em] text-mist/75 transition-colors duration-200 hover:border-brass/40 hover:text-chalk"
              >
                {s}
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-24 sm:px-8 lg:grid-cols-2 lg:px-12 lg:py-32">
          {art.map((card, i) => (
            <Reveal key={card.overline} delay={i * 0.1}>
              <article className="h-full border border-white/[0.08] bg-ink p-8 lg:p-10">
                <p className="overline-label">{card.overline}</p>
                <h3 className="mt-4 font-display text-xl leading-snug text-chalk">{card.title}</h3>
                <div className="mt-5 space-y-4 text-sm leading-relaxed text-mist/85">
                  <p>{card.para1}</p>
                  <p className="text-slate2">{card.para2}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal className="max-w-3xl">
          <p className="overline-label">{t("approach.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
            {t("approach.titleA")}
            <span className="font-serif italic text-brass">{t("approach.titleB")}</span>
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-mist/85">{t("approach.lead")}</p>
          <Link
            to="/investissements"
            data-testid="portfolio-process-link"
            className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brass transition-colors duration-200 hover:text-chalk"
          >
            {t("approach.link")} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl">
              {t("final.titleA")}
              <span className="font-serif italic text-brass">{t("final.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("final.lead")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="portfolio-cta-appointment" className="btn-brass">
                {t("final.btnPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/investissements" data-testid="portfolio-cta-investments" className="btn-ghost">
                {t("final.btnSecondary")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
